import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  EMPTY_LEVEL_SNAPSHOT,
  type AccountLevelSnapshot,
  type DuelXpResult,
  type LevelReward,
} from '@shared/index'
import { useBanlistRewardStore } from './banlistReward'

function copySnap(snap: AccountLevelSnapshot): AccountLevelSnapshot {
  return {
    xp: snap.xp,
    level: snap.level,
    xpIntoLevel: snap.xpIntoLevel,
    xpToNext: snap.xpToNext,
    nextReward: snap.nextReward
      ? { coins: snap.nextReward.coins, packs: snap.nextReward.packs }
      : null,
  }
}

/**
 * Account level (not ranked league). Hydrated from /account/state.
 */
export const useProgressStore = defineStore('progress', () => {
  const xp = ref(0)
  const level = ref(1)
  const xpIntoLevel = ref(0)
  const xpToNext = ref(40)
  const nextReward = ref<LevelReward | null>(EMPTY_LEVEL_SNAPSHOT.nextReward)
  const lastDuel = ref<DuelXpResult | null>(null)

  const barPct = computed(() => {
    const need = xpToNext.value
    if (need <= 0) return 100
    return Math.min(100, Math.round((xpIntoLevel.value / need) * 100))
  })

  function applySnapshot(snap: AccountLevelSnapshot): void {
    const next = copySnap(snap)
    xp.value = next.xp
    level.value = next.level
    xpIntoLevel.value = next.xpIntoLevel
    xpToNext.value = next.xpToNext
    nextReward.value = next.nextReward
  }

  function applyDuelResult(result: DuelXpResult): void {
    lastDuel.value = result
    applySnapshot(result)
    if (result.banlistReward) {
      useBanlistRewardStore().apply(result.banlistReward, (result.banlistPicks ?? 0) > 0)
    }
  }

  function clear(): void {
    applySnapshot(EMPTY_LEVEL_SNAPSHOT)
    lastDuel.value = null
  }

  return {
    xp,
    level,
    xpIntoLevel,
    xpToNext,
    nextReward,
    lastDuel,
    barPct,
    applySnapshot,
    applyDuelResult,
    clear,
  }
})
